import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import PageHeader from '../../../components/common/PageHeader';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PERIOD_MODE_OPTIONS = [
  { value: 'daily_window', label: 'Fenêtre quotidienne 07:00 -> 07:00' },
  { value: 'calendar_day', label: 'Journée calendaire complète' },
  { value: 'previous_calendar_month', label: 'Mois calendaire précédent' },
  { value: 'monthly_day_range', label: 'Cycle mensuel du jour X au jour Y' },
  { value: 'custom_periods', label: 'Périodes fixes personnalisées' },
  { value: 'relative_periods', label: 'Périodes relatives au moment du cron' },
];

const buildDefaultPeriod = (label = 'Période 1') => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(7, 0, 0, 0);

  const start = new Date(end);
  start.setDate(start.getDate() - 1);

  return {
    label,
    startDate: toLocalDatetimeValue(start),
    endDate: toLocalDatetimeValue(end),
  };
};

const buildDefaultRelativePeriod = (label = 'Période 1') => ({
  label,
  startOffsetDays: -1,
  startHour: 7,
  startMinute: 0,
  endOffsetDays: 0,
  endHour: 7,
  endMinute: 0,
});

const buildDefaultCronForm = () => ({
  id: null,
  name: '',
  description: '',
  cronExpression: '0 7 * * *',
  timezone: 'Indian/Reunion',
  enabled: true,
  payload: {
    periodMode: 'daily_window',
    title: 'Rapport ADV planifié',
    subject: 'Rapport ADV planifié',
    to: 'adv@toolbox.re',
    startDayOfMonth: 28,
    endDayOfMonth: 28,
    boundaryHour: 0,
    boundaryMinute: 0,
    periods: [buildDefaultRelativePeriod()],
  },
});

function toLocalDatetimeValue(date) {
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
  return localDate.toISOString().slice(0, 16);
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR');
}

function CronStatusChip({ task }) {
  if (task.lastRunStatus === 'success') {
    return <Chip size="small" color="success" label="OK" />;
  }

  if (task.lastRunStatus === 'error') {
    return <Chip size="small" color="error" label="Erreur" />;
  }

  return <Chip size="small" variant="outlined" label="Jamais lancé" />;
}

function getPeriodModeLabel(periodMode) {
  return PERIOD_MODE_OPTIONS.find((option) => option.value === periodMode)?.label || periodMode;
}

export default function ReportADVPage() {
  const [periods, setPeriods] = useState([buildDefaultPeriod()]);
  const [title, setTitle] = useState('Rapport ADV');
  const [subject, setSubject] = useState('Rapport ADV');
  const [recipients, setRecipients] = useState('adv@toolbox.re');
  const [previewHtml, setPreviewHtml] = useState('');
  const [reportMeta, setReportMeta] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [cronTasks, setCronTasks] = useState([]);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronSaving, setCronSaving] = useState(false);
  const [cronRunningId, setCronRunningId] = useState('');
  const [cronDeletingId, setCronDeletingId] = useState('');
  const [cronError, setCronError] = useState('');
  const [cronSuccess, setCronSuccess] = useState('');
  const [cronForm, setCronForm] = useState(buildDefaultCronForm);

  const previewPayload = useMemo(
    () => ({
      title,
      subject,
      to: recipients,
      periods: periods.map((period) => ({
        label: period.label,
        startDate: period.startDate,
        endDate: period.endDate,
      })),
    }),
    [periods, recipients, subject, title]
  );

  useEffect(() => {
    fetchCronTasks();
  }, []);

  const fetchCronTasks = async () => {
    setCronLoading(true);
    setCronError('');

    try {
      const { data } = await api.get('/api/report/cron-tasks');
      setCronTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      setCronError(err.response?.data?.message || err.message || 'Impossible de charger les cron.');
    } finally {
      setCronLoading(false);
    }
  };

  const updatePeriod = (index, field, value) => {
    setPeriods((prev) =>
      prev.map((period, periodIndex) =>
        periodIndex === index ? { ...period, [field]: value } : period
      )
    );
  };

  const addPeriod = () => {
    setPeriods((prev) => [...prev, buildDefaultPeriod(`Période ${prev.length + 1}`)]);
  };

  const removePeriod = (index) => {
    setPeriods((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await api.post('/api/report/previewADV', previewPayload);
      setPreviewHtml(data.html || '');
      setReportMeta(data.report || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Impossible de générer l’aperçu.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await api.post('/api/report/sendADV', previewPayload);
      setSuccess(data.message || 'Rapport envoyé.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Impossible d’envoyer le rapport.');
    } finally {
      setSending(false);
    }
  };

  const updateCronForm = (field, value) => {
    setCronForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateCronPayload = (field, value) => {
    setCronForm((prev) => ({
      ...prev,
      payload: {
        ...prev.payload,
        [field]: value,
      },
    }));
  };

  const setCronPeriods = (updater) => {
    setCronForm((prev) => ({
      ...prev,
      payload: {
        ...prev.payload,
        periods: updater(prev.payload.periods || []),
      },
    }));
  };

  const addCronPeriod = () => {
    setCronPeriods((prev) => {
      const nextIndex = prev.length + 1;
      if (cronForm.payload.periodMode === 'relative_periods') {
        return [...prev, buildDefaultRelativePeriod(`Période ${nextIndex}`)];
      }
      return [...prev, buildDefaultPeriod(`Période ${nextIndex}`)];
    });
  };

  const removeCronPeriod = (index) => {
    setCronPeriods((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateCronPeriod = (index, field, value) => {
    setCronPeriods((prev) =>
      prev.map((period, periodIndex) =>
        periodIndex === index ? { ...period, [field]: value } : period
      )
    );
  };

  const resetCronForm = () => {
    setCronForm(buildDefaultCronForm());
  };

  const loadCronTaskInForm = (task) => {
    setCronForm({
      id: task._id,
      name: task.name || '',
      description: task.description || '',
      cronExpression: task.cronExpression || '0 7 * * *',
      timezone: task.timezone || 'Indian/Reunion',
      enabled: task.enabled !== false,
      payload: {
        periodMode: task.payload?.periodMode || 'daily_window',
        title: task.payload?.title || 'Rapport ADV planifié',
        subject: task.payload?.subject || 'Rapport ADV planifié',
        to: task.payload?.to || 'adv@toolbox.re',
        startDayOfMonth: Number(task.payload?.startDayOfMonth ?? 28),
        endDayOfMonth: Number(task.payload?.endDayOfMonth ?? 28),
        boundaryHour: Number(task.payload?.boundaryHour ?? 0),
        boundaryMinute: Number(task.payload?.boundaryMinute ?? 0),
        periods: Array.isArray(task.payload?.periods) && task.payload.periods.length
          ? task.payload.periods.map((period, index) => {
              if ((task.payload?.periodMode || 'daily_window') === 'relative_periods') {
                return {
                  label: period.label || `Période ${index + 1}`,
                  startOffsetDays: Number(period.startOffsetDays ?? -1),
                  startHour: Number(period.startHour ?? 7),
                  startMinute: Number(period.startMinute ?? 0),
                  endOffsetDays: Number(period.endOffsetDays ?? 0),
                  endHour: Number(period.endHour ?? 7),
                  endMinute: Number(period.endMinute ?? 0),
                };
              }

              return {
                label: period.label || `Période ${index + 1}`,
                startDate: period.startDate ? toLocalDatetimeValue(period.startDate) : buildDefaultPeriod().startDate,
                endDate: period.endDate ? toLocalDatetimeValue(period.endDate) : buildDefaultPeriod().endDate,
              };
            })
          : [(task.payload?.periodMode || 'daily_window') === 'relative_periods' ? buildDefaultRelativePeriod() : buildDefaultPeriod()],
      },
    });
    setCronError('');
    setCronSuccess('');
  };

  const normalizeCronPayloadForApi = () => {
    const base = {
      periodMode: cronForm.payload.periodMode,
      title: cronForm.payload.title,
      subject: cronForm.payload.subject,
      to: cronForm.payload.to,
      startDayOfMonth: Number(cronForm.payload.startDayOfMonth ?? 28),
      endDayOfMonth: Number(cronForm.payload.endDayOfMonth ?? 28),
      boundaryHour: Number(cronForm.payload.boundaryHour ?? 0),
      boundaryMinute: Number(cronForm.payload.boundaryMinute ?? 0),
    };

    if (cronForm.payload.periodMode === 'relative_periods') {
      return {
        ...base,
        periods: (cronForm.payload.periods || []).map((period) => ({
          label: period.label,
          startOffsetDays: Number(period.startOffsetDays),
          startHour: Number(period.startHour),
          startMinute: Number(period.startMinute),
          endOffsetDays: Number(period.endOffsetDays),
          endHour: Number(period.endHour),
          endMinute: Number(period.endMinute),
        })),
      };
    }

    if (cronForm.payload.periodMode === 'custom_periods') {
      return {
        ...base,
        periods: (cronForm.payload.periods || []).map((period) => ({
          label: period.label,
          startDate: period.startDate,
          endDate: period.endDate,
        })),
      };
    }

    return base;
  };

  const handleSaveCronTask = async () => {
    setCronSaving(true);
    setCronError('');
    setCronSuccess('');

    const payload = {
      name: cronForm.name,
      description: cronForm.description,
      taskType: 'report_adv',
      cronExpression: cronForm.cronExpression,
      timezone: cronForm.timezone,
      enabled: cronForm.enabled,
      payload: normalizeCronPayloadForApi(),
    };

    try {
      if (cronForm.id) {
        await api.put(`/api/report/cron-tasks/${cronForm.id}`, payload);
        setCronSuccess('Cron mis à jour.');
      } else {
        await api.post('/api/report/cron-tasks', payload);
        setCronSuccess('Cron créé.');
      }

      resetCronForm();
      await fetchCronTasks();
    } catch (err) {
      setCronError(err.response?.data?.message || err.message || 'Impossible d’enregistrer le cron.');
    } finally {
      setCronSaving(false);
    }
  };

  const handleRunCronTask = async (taskId) => {
    setCronRunningId(taskId);
    setCronError('');
    setCronSuccess('');

    try {
      const { data } = await api.post(`/api/report/cron-tasks/${taskId}/run`);
      setCronSuccess(data.message || 'Cron exécuté.');
      await fetchCronTasks();
    } catch (err) {
      setCronError(err.response?.data?.message || err.message || 'Impossible de lancer le cron.');
    } finally {
      setCronRunningId('');
    }
  };

  const handleDeleteCronTask = async (taskId) => {
    setCronDeletingId(taskId);
    setCronError('');
    setCronSuccess('');

    try {
      await api.delete(`/api/report/cron-tasks/${taskId}`);
      setCronSuccess('Cron supprimé.');
      if (cronForm.id === taskId) {
        resetCronForm();
      }
      await fetchCronTasks();
    } catch (err) {
      setCronError(err.response?.data?.message || err.message || 'Impossible de supprimer le cron.');
    } finally {
      setCronDeletingId('');
    }
  };

  const handleToggleCronTask = async (task) => {
    setCronSaving(true);
    setCronError('');
    setCronSuccess('');

    try {
      await api.put(`/api/report/cron-tasks/${task._id}`, {
        name: task.name,
        description: task.description,
        taskType: task.taskType,
        cronExpression: task.cronExpression,
        timezone: task.timezone,
        enabled: !task.enabled,
        payload: task.payload,
      });
      setCronSuccess(`Cron ${!task.enabled ? 'activé' : 'désactivé'}.`);
      await fetchCronTasks();
    } catch (err) {
      setCronError(err.response?.data?.message || err.message || 'Impossible de modifier l’état du cron.');
    } finally {
      setCronSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: '#f8fafc', minHeight: '100%' }}>
      <Stack spacing={3}>
        <PageHeader section="Administratif" title="Reports" />

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Stack spacing={3} sx={{ flex: 1.1 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Envoi manuel
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Génère un aperçu web puis envoie le même rendu par email.
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Titre" value={title} onChange={(event) => setTitle(event.target.value)} />
                  <TextField fullWidth label="Sujet du mail" value={subject} onChange={(event) => setSubject(event.target.value)} />
                  <TextField
                    fullWidth
                    label="Destinataires"
                    helperText="Séparez plusieurs adresses par des virgules."
                    value={recipients}
                    onChange={(event) => setRecipients(event.target.value)}
                  />
                </Stack>

                <Divider />

                <Stack spacing={2}>
                  {periods.map((period, index) => (
                    <Paper key={`${index}-${period.label}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Période {index + 1}
                          </Typography>
                          <IconButton onClick={() => removePeriod(index)} disabled={periods.length === 1} color="error">
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                        <TextField
                          label="Libellé"
                          value={period.label}
                          onChange={(event) => updatePeriod(index, 'label', event.target.value)}
                          fullWidth
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Début"
                            type="datetime-local"
                            value={period.startDate}
                            onChange={(event) => updatePeriod(index, 'startDate', event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Fin"
                            type="datetime-local"
                            value={period.endDate}
                            onChange={(event) => updatePeriod(index, 'endDate', event.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button startIcon={<AddIcon />} variant="outlined" onClick={addPeriod}>
                    Ajouter une période
                  </Button>
                  <Button
                    startIcon={loadingPreview ? <CircularProgress size={18} /> : <PreviewIcon />}
                    variant="contained"
                    onClick={handlePreview}
                    disabled={loadingPreview}
                  >
                    Prévisualiser
                  </Button>
                  <Button
                    startIcon={sending ? <CircularProgress size={18} /> : <MailOutlineIcon />}
                    variant="contained"
                    color="success"
                    onClick={handleSend}
                    disabled={sending}
                  >
                    Envoyer le mail
                  </Button>
                </Stack>

                {error ? <Alert severity="error">{error}</Alert> : null}
                {success ? <Alert severity="success">{success}</Alert> : null}
                {reportMeta ? (
                  <Alert severity="info">
                    {reportMeta.periods?.length || 0} période(s), {reportMeta.totalEntries || 0} évènement(s).
                  </Alert>
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Aperçu web
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Le rendu affiché ici est identique à l’HTML envoyé par email.
                </Typography>
              </Box>
              <Box sx={{ minHeight: 220, background: '#f8fafc' }}>
                {loadingPreview ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
                    <CircularProgress />
                  </Stack>
                ) : previewHtml ? (
                  <Box dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 220, px: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Lancez une prévisualisation pour afficher le rapport.
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Stack>

          <Stack spacing={3} sx={{ flex: 1 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Tâche cron
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Créez ou modifiez un envoi automatique de report ADV.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={resetCronForm}>
                    Nouveau
                  </Button>
                </Stack>

                <TextField label="Nom" value={cronForm.name} onChange={(event) => updateCronForm('name', event.target.value)} fullWidth />
                <TextField
                  label="Description"
                  value={cronForm.description}
                  onChange={(event) => updateCronForm('description', event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Expression cron"
                    value={cronForm.cronExpression}
                    onChange={(event) => updateCronForm('cronExpression', event.target.value)}
                    fullWidth
                    helperText="Format: minute heure jour mois semaine"
                  />
                  <TextField
                    label="Timezone"
                    value={cronForm.timezone}
                    onChange={(event) => updateCronForm('timezone', event.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Mode de période"
                    value={cronForm.payload.periodMode}
                    onChange={(event) =>
                      updateCronPayload('periodMode', event.target.value)
                    }
                    fullWidth
                  >
                    {PERIOD_MODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160 }}>
                    <Typography variant="body2">Actif</Typography>
                    <Switch
                      checked={cronForm.enabled}
                      onChange={(event) => updateCronForm('enabled', event.target.checked)}
                    />
                  </Stack>
                </Stack>

                <TextField
                  label="Titre du report"
                  value={cronForm.payload.title}
                  onChange={(event) => updateCronPayload('title', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Sujet du mail"
                  value={cronForm.payload.subject}
                  onChange={(event) => updateCronPayload('subject', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Destinataires"
                  value={cronForm.payload.to}
                  onChange={(event) => updateCronPayload('to', event.target.value)}
                  helperText="Une ou plusieurs adresses séparées par des virgules."
                  fullWidth
                />

                {cronForm.payload.periodMode === 'monthly_day_range' ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      label="Jour de début"
                      type="number"
                      value={cronForm.payload.startDayOfMonth}
                      onChange={(event) => updateCronPayload('startDayOfMonth', event.target.value)}
                      helperText="Ex: 28"
                      fullWidth
                    />
                    <TextField
                      label="Jour de fin"
                      type="number"
                      value={cronForm.payload.endDayOfMonth}
                      onChange={(event) => updateCronPayload('endDayOfMonth', event.target.value)}
                      helperText="Ex: 28"
                      fullWidth
                    />
                    <TextField
                      label="Heure frontière"
                      type="number"
                      value={cronForm.payload.boundaryHour}
                      onChange={(event) => updateCronPayload('boundaryHour', event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Minute frontière"
                      type="number"
                      value={cronForm.payload.boundaryMinute}
                      onChange={(event) => updateCronPayload('boundaryMinute', event.target.value)}
                      fullWidth
                    />
                  </Stack>
                ) : null}

                {cronForm.payload.periodMode === 'custom_periods' || cronForm.payload.periodMode === 'relative_periods' ? (
                  <>
                    <Divider />
                    <Stack spacing={2}>
                      {(cronForm.payload.periods || []).map((period, index) => (
                        <Paper key={`${index}-${period.label}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Période cron {index + 1}
                              </Typography>
                              <IconButton
                                onClick={() => removeCronPeriod(index)}
                                disabled={(cronForm.payload.periods || []).length === 1}
                                color="error"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Stack>

                            <TextField
                              label="Libellé"
                              value={period.label}
                              onChange={(event) => updateCronPeriod(index, 'label', event.target.value)}
                              fullWidth
                            />

                            {cronForm.payload.periodMode === 'custom_periods' ? (
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                  label="Début"
                                  type="datetime-local"
                                  value={period.startDate}
                                  onChange={(event) => updateCronPeriod(index, 'startDate', event.target.value)}
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                  label="Fin"
                                  type="datetime-local"
                                  value={period.endDate}
                                  onChange={(event) => updateCronPeriod(index, 'endDate', event.target.value)}
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Stack>
                            ) : (
                              <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <TextField
                                    label="Décalage début (jours)"
                                    type="number"
                                    value={period.startOffsetDays}
                                    onChange={(event) => updateCronPeriod(index, 'startOffsetDays', event.target.value)}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Heure début"
                                    type="number"
                                    value={period.startHour}
                                    onChange={(event) => updateCronPeriod(index, 'startHour', event.target.value)}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Minute début"
                                    type="number"
                                    value={period.startMinute}
                                    onChange={(event) => updateCronPeriod(index, 'startMinute', event.target.value)}
                                    fullWidth
                                  />
                                </Stack>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <TextField
                                    label="Décalage fin (jours)"
                                    type="number"
                                    value={period.endOffsetDays}
                                    onChange={(event) => updateCronPeriod(index, 'endOffsetDays', event.target.value)}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Heure fin"
                                    type="number"
                                    value={period.endHour}
                                    onChange={(event) => updateCronPeriod(index, 'endHour', event.target.value)}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Minute fin"
                                    type="number"
                                    value={period.endMinute}
                                    onChange={(event) => updateCronPeriod(index, 'endMinute', event.target.value)}
                                    fullWidth
                                  />
                                </Stack>
                              </>
                            )}
                          </Stack>
                        </Paper>
                      ))}

                      <Button startIcon={<AddIcon />} variant="outlined" onClick={addCronPeriod}>
                        Ajouter une période cron
                      </Button>
                    </Stack>
                  </>
                ) : null}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    startIcon={cronSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                    variant="contained"
                    onClick={handleSaveCronTask}
                    disabled={cronSaving}
                  >
                    {cronForm.id ? 'Mettre à jour' : 'Créer le cron'}
                  </Button>
                  {cronForm.id ? (
                    <Button variant="outlined" onClick={resetCronForm}>
                      Annuler l’édition
                    </Button>
                  ) : null}
                </Stack>

                {cronError ? <Alert severity="error">{cronError}</Alert> : null}
                {cronSuccess ? <Alert severity="success">{cronSuccess}</Alert> : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Cron enregistrés
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Liste des tâches automatiques stockées en base.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={fetchCronTasks} disabled={cronLoading}>
                    Rafraîchir
                  </Button>
                </Stack>

                {cronLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </Stack>
                ) : cronTasks.length ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nom</TableCell>
                          <TableCell>Planification</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Dernier run</TableCell>
                          <TableCell>Actif</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cronTasks.map((task) => (
                          <TableRow key={task._id} hover>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {task.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {getPeriodModeLabel(task.payload?.periodMode || 'daily_window')}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{task.cronExpression}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {task.timezone || 'Indian/Reunion'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.75}>
                                <CronStatusChip task={task} />
                                <Typography variant="caption" color="text.secondary">
                                  {task.lastRunMessage || '—'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{formatDateTime(task.lastRunAt)}</TableCell>
                            <TableCell>
                              <Switch checked={task.enabled !== false} onChange={() => handleToggleCronTask(task)} />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <IconButton color="primary" onClick={() => loadCronTaskInForm(task)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="success"
                                  onClick={() => handleRunCronTask(task._id)}
                                  disabled={cronRunningId === task._id}
                                >
                                  {cronRunningId === task._id ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteCronTask(task._id)}
                                  disabled={cronDeletingId === task._id}
                                >
                                  {cronDeletingId === task._id ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Alert severity="info">Aucune tâche cron enregistrée pour le moment.</Alert>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
